import { useEffect, useRef, useState } from 'react';
import type { ComponentCheck } from './hooks/useTierEvaluation';
import type { PreviousTierInfo } from './hooks/useTierEvaluation';
import { serviceContainer } from '../../../services/ServiceContainer';
import { machineIdState } from '../../../api/stores/shared';
import { TierCard } from './components/TierCard';
import { getTierBorderGradient } from './utils/tierGradients';

interface TierDisplayInfo {
  tierId: string;
  name: string;
  earnRateDescription?: string;
  maxEarningsDescription?: string;
}

interface NextTierDisplayInfo extends TierDisplayInfo {
  componentChecks: ComponentCheck[];
}

interface WelcomeToPlaycastProps {
  tier: TierDisplayInfo | null;
  nextTier: NextTierDisplayInfo | null;
  previousTier: PreviousTierInfo | null;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  onContinue: () => void;
  onLearnMore: () => void;
}

const DISCORD_LINK = 'https://discord.gg/playcast';

const EVAL_STEPS = [
  { label: 'GPU', durationMs: 6000 },
  { label: 'CPU', durationMs: 5500 },
  { label: 'RAM', durationMs: 4500 },
  { label: 'Storage', durationMs: 5000 },
  { label: 'Internet Speed', durationMs: 18000 },
  { label: 'Video Encoding', durationMs: 5000 },
  { label: 'Environment', durationMs: 4000 },
] as const;

function LoadingScreen() {
  const [stepIdx, setStepIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const stepStartRef = useRef(Date.now());
  const currentStep = EVAL_STEPS[stepIdx % EVAL_STEPS.length];

  useEffect(() => {
    stepStartRef.current = Date.now();
    setProgress(0);

    const duration = currentStep.durationMs;
    const interval = setInterval(() => {
      const elapsed = Date.now() - stepStartRef.current;
      const pct = Math.min(elapsed / duration, 1);
      setProgress(pct);
      if (pct >= 1) {
        clearInterval(interval);
        setTimeout(() => setStepIdx((i) => i + 1), 300);
      }
    }, 50);
    return () => clearInterval(interval);
  }, [stepIdx]);

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: '24px 20px',
        background: '#0b0618',
        gap: 24,
      }}
    >
      <img
        src="/playcast-gibber.svg"
        alt="Playcast"
        style={{ width: 139, height: 112, objectFit: 'contain', flexShrink: 0 }}
      />

      <div style={{ textAlign: 'center' }}>
        <p style={{ margin: 0, color: '#ffffff', fontSize: 15, fontWeight: 600 }}>
          Evaluating your PC
        </p>
        <p style={{ margin: '6px 0 0', color: '#8d8aa8', fontSize: 11, lineHeight: '15px' }}>
          This determines your earning tier — may take up to 2 minutes
        </p>
      </div>

      <div style={{ width: '100%', maxWidth: 240 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
          <p style={{ margin: 0, color: '#ffffff', fontSize: 13, fontWeight: 600 }}>
            {currentStep.label} Test
          </p>
          <p style={{ margin: 0, color: '#FBBF24', fontSize: 11, fontWeight: 500 }}>
            Running…
          </p>
        </div>

        <div
          style={{
            width: '100%',
            height: 6,
            borderRadius: 3,
            background: 'rgba(255,255,255,0.08)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              borderRadius: 3,
              background: 'linear-gradient(90deg, #7c3aed, #FBBF24)',
              width: `${Math.round(progress * 100)}%`,
              transition: 'width 60ms linear',
            }}
          />
        </div>
      </div>
    </div>
  );
}

function ErrorScreen({ onRetry }: { onRetry?: () => void }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: '24px 16px',
        background: '#0b0618',
        gap: 16,
      }}
    >
      <img
        src="/playcast-gibber.svg"
        alt=""
        style={{ width: 48, height: 48 }}
      />
      <p style={{ margin: 0, color: '#ffffff', fontSize: 16, fontWeight: 700, textAlign: 'center' }}>
        Unable to evaluate this PC
      </p>
      <p style={{ margin: 0, color: '#8d8aa8', fontSize: 13, lineHeight: '18px', textAlign: 'center', maxWidth: 280 }}>
        We couldn't retrieve your PC's hardware evaluation. Please try again, or reach out to us on{' '}
        <a
          href={DISCORD_LINK}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#FBBF24', textDecoration: 'underline' }}
        >
          Discord
        </a>
        {' '}for help.
      </p>
      {onRetry && (
        <button
          type="button"
          onClick={() => {
            onRetry();
            serviceContainer.analyticsManager.sendOnboardingWelcomeViewedEvent({
              tier: 'timeout-retry',
              machineId: machineIdState.currentState,
            });
          }}
          style={{
            marginTop: 8,
            padding: '10px 24px',
            borderRadius: 999,
            border: '1px solid #FBBF24',
            background: 'transparent',
            color: '#FBBF24',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          Try Again
        </button>
      )}
    </div>
  );
}

export const WelcomeToPlaycast = ({
  tier,
  nextTier,
  previousTier,
  loading,
  onRetry,
  onContinue,
  onLearnMore,
}: WelcomeToPlaycastProps) => {
  const mountTimeRef = useRef(Date.now());
  const analyticsSentRef = useRef(false);

  // Only fire the analytics event once tier data is loaded and the welcome content is shown
  useEffect(() => {
    if (loading || analyticsSentRef.current) return;
    analyticsSentRef.current = true;
    serviceContainer.analyticsManager.sendOnboardingWelcomeViewedEvent({
      tier: tier?.tierId ?? 'unknown',
      machineId: machineIdState.currentState,
    });
  }, [loading, tier?.tierId]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!tier) {
    return <ErrorScreen onRetry={onRetry} />;
  }

  const handleContinue = () => {
    serviceContainer.analyticsManager.sendOnboardingWelcomeCompletedEvent({
      tier: tier?.tierId ?? 'unknown',
      viewDurationMs: Date.now() - mountTimeRef.current,
    });
    onContinue();
  };

  const currentTierId = tier?.tierId ?? 'tier-1';

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
        padding: '8px 0px',
        background: '#0b0618',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%', alignItems: 'center', flexShrink: 0 }}>
        <img
          src="/playcast-gibber.svg"
          alt=""
          style={{ width: 48, height: 48, marginBottom: 4 }}
        />
        <p style={{ margin: 0, color: '#ffffff', fontSize: 20, lineHeight: '24px', fontWeight: 700, textAlign: 'center' }}>
          Welcome to<br /><span style={{ color: '#FBBF24' }}>Playcast Rewards!</span>
        </p>
        <p style={{ margin: '8px 0 0', color: '#8d8aa8', fontSize: 12, lineHeight: '16px', fontWeight: 500, textAlign: 'center', width: '100%' }}>
          You're just a few steps from adding this PC to the Playcast network — and earning Playcast Tokens for every hour it hosts.
        </p>
      </div>

      {/* Tier cards */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', paddingTop: 24, gap: 8, width: '100%' }}>
        {tier && (
          <TierCard
            name={tier.name}
            earnRateDescription={tier.earnRateDescription}
            maxEarningsDescription={tier.maxEarningsDescription}
            variant="current"
            borderGradient={getTierBorderGradient(currentTierId)}
          />
        )}

        {nextTier && (
          <TierCard
            name={nextTier.name}
            earnRateDescription={nextTier.earnRateDescription}
            maxEarningsDescription={nextTier.maxEarningsDescription}
            variant="next"
            onLearnMore={onLearnMore}
          />
        )}

        {!nextTier && previousTier && (
          <TierCard
            name={previousTier.name}
            earnRateDescription={previousTier.earnRateDescription}
            maxEarningsDescription={previousTier.maxEarningsDescription}
            variant="previous"
          />
        )}
      </div>

      {/* Continue button — only shown when loaded */}
      <div style={{ width: '100%', flexShrink: 0, paddingTop: 8 }}>
        <button
          type="button"
          onClick={handleContinue}
          style={{
            width: '100%',
            minHeight: 44,
            padding: '12px 16px',
            borderRadius: 999,
            border: 'none',
            background: '#FBBF24',
            color: '#141414',
            cursor: 'pointer',
            fontWeight: 700,
            fontSize: 15,
          }}
        >
          Continue
        </button>
      </div>
    </div>
  );
};
